import { AxiosResponse } from 'axios';
import { apiClient, getAuthHeaders } from './index';
import { OpinionItemType, TokenStatus } from '../../stores/data/types';
import { TokenState } from './types';

export const evaluationApi = {
  /**
   * 평가자 의견을 추가합니다. (3명 이상 평가 시 자동으로 "Ready for IC" 상태 전환)
   * @param tokenAddress 토큰 주소
   * @param request 평가자 의견 정보
   * @returns 업데이트된 Token State
   */
  async addEvaluatorOpinion(
    tokenAddress: string,
    request: {
      name: string;
      items: Array<{
        type: OpinionItemType;
        score: number; // 1-5
        comment?: string;
      }>;
      comment?: string;
    }
  ): Promise<TokenState> {
    try {
      const response: AxiosResponse<any> = await apiClient.post(
        `/fe-management-system/token-states/${tokenAddress}/evaluation`,
        {
          name: request.name,
          items: request.items,
          comment: request.comment,
        },
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data.data;
    } catch (error) {
      console.log(error)
      console.error('❌ POST /fe-management-system/token-states/:tokenAddress/evaluation 에러:', error);
      throw error;
    }
  },

  /**
   * 기존 평가자 의견을 수정합니다. (RFC 7386 JSON Merge Patch 지원)
   * @param tokenAddress 토큰 주소
   * @param name 평가자 이름
   * @param request 수정할 평가자 의견 정보
   * @returns 업데이트된 Token State
   */
  async updateEvaluatorOpinion(
    tokenAddress: string,
    name: string,
    request: {
      items?: Array<{
        type: OpinionItemType;
        score: number; // 1-5
        comment?: string;
      }>;
      comment?: string | null;
    }
  ): Promise<TokenState> {
    try {
      const response: AxiosResponse<any> = await apiClient.patch(
        `/fe-management-system/token-states/${tokenAddress}/evaluation/${encodeURIComponent(name)}`,
        {
          items: request.items,
          comment: request.comment,
        },
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('❌ PATCH /fe-management-system/token-states/:tokenAddress/evaluation/:name 에러:', error);
      throw error;
    }
  },

  /**
   * 평가 요약을 업데이트합니다.
   * @param tokenAddress 토큰 주소
   * @param summary 평가 요약
   * @returns 업데이트된 Token State
   */
  async updateEvaluationSummary(
    tokenAddress: string,
    summary: string
  ): Promise<TokenState> {
    try {
      const response: AxiosResponse<any> = await apiClient.patch(
        `/fe-management-system/token-states/${tokenAddress}/evaluation/summary`,
        { summary },
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('❌ PATCH /fe-management-system/token-states/:tokenAddress/evaluation/summary 에러:', error);
      throw error;
    }
  },

  /**
   * 회고를 업데이트합니다.
   * @param tokenAddress 토큰 주소
   * @param retrospective 회고 내용
   * @returns 업데이트된 Token State
   */
  async updateRetrospective(
    tokenAddress: string,
    retrospective: string
  ): Promise<TokenState> {
    try {
      const response: AxiosResponse<any> = await apiClient.patch(
        `/fe-management-system/token-states/${tokenAddress}/evaluation/retrospective`,
        { retrospective },
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('❌ PATCH /fe-management-system/token-states/:tokenAddress/evaluation/retrospective 에러:', error);
      throw error;
    }
  },

  /**
   * IC 의사결정 상태를 업데이트합니다.
   * @param tokenAddress 토큰 주소
   * @param decision 적용할 TokenStatus (Buy/Not Buy/Hold/Later)
   * @returns 업데이트된 Token State
   */
  async updateDecision(
    tokenAddress: string,
    decision: TokenStatus
  ): Promise<TokenState> {
    try {
      const response: AxiosResponse<any> = await apiClient.patch(
        `/fe-management-system/token-states/${tokenAddress}/decision`,
        { decision },
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('❌ PATCH /fe-management-system/token-states/:tokenAddress/decision 에러:', error);
      throw error;
    }
  },
};
